﻿import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/contexts/DataContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { api } from "@/api";
import { BarChart3, Lock, Calendar, Settings, Link2, Wifi, CheckCircle2, ChevronDown, ChevronUp, Copy, Check } from "lucide-react";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

const MetaLogo = ({ className = "" }) => (
  <div
    className={
      "inline-flex items-center justify-center rounded-full bg-blue-50 text-[10px] font-semibold text-blue-600 " +
      className
    }
  >
    <span>Meta</span>
  </div>
);

const ShoreLogo = ({ className = "" }) => (
  <div
    className={
      "inline-flex items-center justify-center rounded-full bg-emerald-50 " +
      className
    }
  >
    <Calendar className="h-5 w-5 text-emerald-600" />
  </div>
);

const IntegrationsPage = () => {
  const { t } = useTranslation();
  const { company } = useAuth();
  const [selected, setSelected] = useState(null);

  const shoreEnabled = !!company?.shoreEnabled;

  const integrations = [
    {
      id: "meta",
      title: t("merchant.integrations.meta.title", "Meta Werbeanzeigenmanager"),
      description: t(
        "merchant.integrations.meta.description",
        "Verbinde dein Meta Werbekonto, um Budget und Reichweite deiner Kampagnen direkt neben deinen Aktionen zu sehen."
      ),
      icon: MetaLogo,
      tagline: t("merchant.integrations.meta.tagline", "Meta / Facebook Ads"),
    },
    {
      id: "meta-capi",
      title: t("capi.title", "Meta Conversions API"),
      description: t(
        "merchant.integrations.meta.capiDescription",
        "Nutze die Meta Conversions API, um serverseitige Events (Scans, Leads, Einl\u00f6sungen) DSGVO-konform an Meta zu senden."
      ),
      icon: MetaLogo,
      tagline: t("merchant.integrations.meta.capiTagline", "Meta Conversion API"),
    },
    {
      id: "shore",
      title: "Shore Terminbuchung",
      description: "Bei jeder Buchung \u00fcber shore.com erh\u00e4lt der Kunde automatisch eine E-Mail mit einem Link zu einer deiner Aktionen (z. B. Willkommensgeschenk).",
      icon: ShoreLogo,
      tagline: "shore.com \u2192 E-Mail bei Buchung",
      locked: !shoreEnabled,
    },
  ];

  if (!selected) {
    return (
      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
        <div>
          <h1 className="text-xl font-semibold mb-1 flex items-center gap-2">
            <span>{t("nav.integrations", "Integrationen")}</span>
          </h1>
          <p className="text-sm text-gray-600">
            {t(
              "merchant.integrations.description",
              "Verwalte hier die Verkn\u00fcpfungen zu externen Plattformen wie z. B. dem Meta Werbeanzeigenmanager."
            )}
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {integrations.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => !item.locked && setSelected(item.id)}
              className={[
                "text-left bg-white rounded-lg shadow-sm border p-4 flex flex-col gap-3 transition-colors relative",
                item.locked
                  ? "opacity-60 cursor-not-allowed"
                  : "hover:border-blue-500",
              ].join(" ")}
            >
              {item.locked && (
                <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] text-muted-foreground border rounded-full px-2 py-0.5 bg-gray-50">
                  <Lock className="h-2.5 w-2.5" />
                  Nicht freigeschaltet
                </div>
              )}
              <div className="flex items-center gap-3">
                <item.icon className="h-10 w-10" />
                <div>
                  <div className="font-medium text-sm">{item.title}</div>
                  <div className="text-[11px] text-gray-500">{item.tagline}</div>
                </div>
              </div>
              <p className="text-xs text-gray-600 line-clamp-3">{item.description}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (selected === "meta") {
    return <MetaIntegrationSettings onBack={() => setSelected(null)} />;
  }

  if (selected === "meta-capi") {
    return <MetaCapiStandalone onBack={() => setSelected(null)} />;
  }

  if (selected === "shore") {
    return <ShoreIntegrationSettings onBack={() => setSelected(null)} />;
  }

  return null;
};

const MetaIntegrationSettings = ({ onBack }) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { actions } = useData();

  // Mapping state (Kampagnen x Aktionen)
  const [metaConnected, setMetaConnected] = useState(false);
  const [savingMappings, setSavingMappings] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncDialog, setSyncDialog] = useState({
    open: false,
    actionId: null,
    actionTitle: "",
    metaCampaignName: "",
  });
  const [campaignSearch, setCampaignSearch] = useState("");

  useEffect(() => {
    if (!token || !actions) return;
    let mounted = true;
    (async () => {
      try {
        setLoadingMappings(true);
        setLoadingCampaigns(true);
        const [existingResp, campaignsResp] = await Promise.all([
          api.getMetaMappings(token),
          api.getMetaCampaigns(token),
        ]);
        if (!mounted) return;
        const existing = Array.isArray(existingResp?.mappings)
          ? existingResp.mappings
          : existingResp || [];
        const campaignsList = Array.isArray(campaignsResp?.campaigns)
          ? campaignsResp.campaigns
          : campaignsResp || [];
        setCampaigns(campaignsList);
        setMetaConnected(campaignsList.length > 0);
        const byAction = new Map((existing || []).map((m) => [m.actionId, m]));
        setMappings(
          actions.map((a) => {
            const existingMapping = byAction.get(a.id);
            return {
              actionId: a.id,
              actionTitle: a.title,
              metaCampaignId: existingMapping?.metaCampaignId || "",
              metaCampaignName: existingMapping?.metaCampaignName || null,
            };
          })
        );
      } catch (e) {
        toast({
          title: t("org.error", { defaultValue: "Fehler" }),
          description: e.message,
          variant: "destructive",
        });
      } finally {
        if (mounted) {
          setLoadingMappings(false);
          setLoadingCampaigns(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [token, actions, t]);

  const handleConnectMeta = async () => {
    if (!token) return;
    try {
      const resp = await api.getMetaOAuthUrl(token);
      const url = resp?.url;
      if (!url) throw new Error("Meta OAuth URL fehlt");
      window.location.href = url;
    } catch (e) {
      const code = e?.payload?.code;
      if (code === "META_OAUTH_NOT_CONFIGURED") {
        toast({
          title: t("org.error", { defaultValue: "Fehler" }),
          description: t(
            "merchant.integrations.meta.oauthNotConfigured",
            "Meta OAuth ist noch nicht serverseitig konfiguriert. Bitte wende dich an den Administrator."
          ),
          variant: "destructive",
        });
      } else {
        toast({
          title: t("org.error", { defaultValue: "Fehler" }),
          description: e.message,
          variant: "destructive",
        });
      }
    }
  };

  const handleChangeMapping = (actionId, metaCampaignId) => {
    setMappings((prev) =>
      prev.map((m) => (m.actionId === actionId ? { ...m, metaCampaignId } : m))
    );
  };

  const handleSaveMappings = async () => {
    if (!token) return;
    setSavingMappings(true);
    try {
      const payload = mappings.map((m) => ({
        actionId: m.actionId,
        metaCampaignId: m.metaCampaignId || null,
        metaCampaignName: m.metaCampaignId
          ? campaigns.find((c) => c.id === m.metaCampaignId)?.name || null
          : null,
      }));
      await api.saveMetaMappings(token, payload);
      toast({
        title: t(
          "merchant.integrations.meta.mappingsSavedTitle",
          "Mappings gespeichert"
        ),
        description: t(
          "merchant.integrations.meta.mappingsSavedDesc",
          "Die Zuordnungen deiner Aktionen wurden gespeichert."
        ),
      });
    } catch (e) {
      toast({
        title: t("org.error", { defaultValue: "Fehler" }),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setSavingMappings(false);
    }
  };

  const openSyncDialogForAction = (m) => {
    if (!m.metaCampaignId) return;
    const campaignName =
      campaigns.find((c) => c.id === m.metaCampaignId)?.name || "";
    setSyncDialog({
      open: true,
      actionId: m.actionId,
      actionTitle: m.actionTitle || `#${m.actionId}`,
      metaCampaignName: campaignName,
    });
  };

  const handleConfirmSyncAction = async () => {
    if (!token || !syncDialog.actionId) return;
    try {
      setSyncing(true);
      const today = new Date();
      const sinceDate = new Date(today.getTime() - 365 * 86400000);
      const since = sinceDate.toISOString().substring(0, 10);
      const until = today.toISOString().substring(0, 10);
      const result = await api.metaSyncForAction(token, {
        actionId: syncDialog.actionId,
        since,
        until,
      });
      const days =
        typeof result?.synced === "number" ? result.synced : undefined;
      toast({
        title: t(
          "merchant.integrations.meta.syncActionSuccessTitle",
          "Meta-Daten f├╝r Aktion synchronisiert"
        ),
        description:
          days != null
            ? t(
                "merchant.integrations.meta.syncActionSuccessDescWithDays",
                "F├╝r diese Aktion wurden {{days}} Tage an Meta-Daten geladen.",
                { days }
              )
            : t(
                "merchant.integrations.meta.syncActionSuccessDesc",
                "Die Meta-Insights f├╝r diese Aktion wurden aus Meta importiert."
              ),
      });
      setSyncDialog({ open: false, actionId: null, actionTitle: "", metaCampaignName: "" });
    } catch (e) {
      toast({
        title: t("org.error", { defaultValue: "Fehler" }),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncMeta = async () => {
    if (!token) return;
    try {
      setSyncing(true);
      const today = new Date();
      const sinceDate = new Date(today.getTime() - 30 * 86400000);
      const since = sinceDate.toISOString().substring(0, 10);
      const until = today.toISOString().substring(0, 10);
      const result = await api.metaSync(token, { since, until });
      const days = typeof result?.synced === "number" ? result.synced : undefined;
      toast({
        title: t(
          "merchant.integrations.meta.syncSuccessTitle",
          "Meta-Daten synchronisiert"
        ),
        description:
          days != null
            ? t(
                "merchant.integrations.meta.syncSuccessDescWithDays",
                "Es wurden {{days}} Tage an Meta-Daten aktualisiert.",
                { days }
              )
            : t(
                "merchant.integrations.meta.syncSuccessDesc",
                "Die Meta-Insights wurden f├╝r deine Kampagnen aktualisiert."
              ),
      });
    } catch (e) {
      toast({
        title: t("org.error", { defaultValue: "Fehler" }),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-blue-600 hover:text-blue-800 mb-1"
      >
        {t("nav.integrations", "Integrationen")}
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <MetaLogo className="h-9 w-9" />
          <div>
            <h1 className="text-xl font-semibold">
              {t("merchant.integrations.meta.title", "Meta Werbeanzeigenmanager")}
            </h1>
            <p className="text-xs text-gray-600">
              {t(
                "merchant.integrations.meta.description",
                "Verbinde dein Meta Werbekonto, um Budget und Reichweite deiner Kampagnen direkt neben deinen Aktionen zu sehen."
              )}
            </p>
          </div>
        </div>
        <div className="text-[11px] text-gray-500 text-right">
          <div>
            {metaConnected
              ? t(
                  "merchant.integrations.meta.statusConnected",
                  "Verbunden"
                )
              : t(
                  "merchant.integrations.meta.statusDisconnected",
                  "Noch nicht verbunden"
                )}
          </div>
          <Button size="xs" className="mt-1" onClick={handleConnectMeta}>
            {t(
              "merchant.integrations.meta.connectButton",
              "Mit Meta verbinden"
            )}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="mapping" className="mt-2">
        <TabsList className="inline-flex h-8 rounded-md border bg-white p-1 text-xs">
          <TabsTrigger value="mapping" className="px-3 py-1">
            {t(
              "merchant.integrations.meta.mappingTitle",
              "Kampagnen & Zuordnung"
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="mapping" className="mt-4 space-y-4">
          <div className="bg-white p-3 sm:p-4 rounded shadow">
            <h3 className="text-sm font-semibold mb-2">
              {t(
                "merchant.integrations.meta.mappingTitle",
                "Kampagnen mit Aktionen verknuepfen"
              )}
            </h3>
            <p className="text-[11px] text-gray-600 mb-3">
              {t(
                "merchant.integrations.meta.mappingDescription",
                "Ordne deine Aktionen den passenden Meta-Kampagnen zu. So koennen wir Budget und Reichweite pro Aktion im Verlauf anzeigen."
              )}
            </p>
            <div className="mb-2 flex justify-end">
              <input
                type="text"
                className="border rounded px-2 py-1 text-[11px] w-full sm:w-64"
                placeholder={t(
                  "merchant.integrations.meta.campaignSearchPlaceholder",
                  "Kampagnen filternÔÇª"
                )}
                value={campaignSearch}
                onChange={(e) => setCampaignSearch(e.target.value)}
              />
            </div>
            <div className="border rounded overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-2 bg-gray-50 text-[11px] font-medium text-gray-600">
                <div className="px-2 py-1 border-b sm:border-b-0 sm:border-r">
                  {t("merchant.integrations.meta.colAction", "Aktion")}
                </div>
                <div className="px-2 py-1">
                  {t(
                    "merchant.integrations.meta.colCampaign",
                    "Meta-Kampagne"
                  )}
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto text-xs">
                {loadingMappings && (
                  <div className="px-2 py-3 text-[11px] text-gray-500">
                    {t("common.loading", "Laden...")}
                  </div>
                )}
                {mappings.map((m) => {
                  const visibleCampaigns = campaignSearch
                    ? campaigns.filter((c) =>
                        String(c.name || "")
                          .toLowerCase()
                          .includes(campaignSearch.toLowerCase())
                      )
                    : campaigns;
                  return (
                    <div
                      key={m.actionId}
                      className="grid grid-cols-1 sm:grid-cols-2 border-t items-start sm:items-center"
                    >
                      <div className="px-2 py-1 text-[11px] truncate">
                        {m.actionTitle || `#${m.actionId}`}
                      </div>
                      <div className="px-2 py-1">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <select
                          className="w-full border rounded px-1 py-1 text-[11px]"
                          value={m.metaCampaignId}
                          onChange={(e) =>
                            handleChangeMapping(m.actionId, e.target.value)
                          }
                        >
                          <option value="">
                            {t(
                              "merchant.integrations.meta.selectPlaceholder",
                              "Keine Zuordnung"
                            )}
                          </option>
                          {visibleCampaigns.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                          {m.metaCampaignId && (
                            <Button
                              type="button"
                              size="xs"
                              variant="outline"
                              onClick={() => openSyncDialogForAction(m)}
                            >
                              {t(
                                "merchant.integrations.meta.syncActionButton",
                                "Jetzt syncen"
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(!mappings || mappings.length === 0) && (
                  <div className="px-2 py-3 text-[11px] text-gray-500">
                    {t(
                      "merchant.integrations.meta.noActions",
                      "Aktuell gibt es keine Aktionen, die verkn├╝pft werden k├Ânnen."
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                size="sm"
                onClick={handleSaveMappings}
                disabled={savingMappings || !mappings.length}
              >
                {savingMappings
                  ? t("common.saving", "SpeichernÔÇª")
                  : t("common.save", "Speichern")}
              </Button>
            </div>
          </div>

          <div className="bg-white p-3 sm:p-4 rounded shadow flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 text-xs text-gray-600">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-4 w-4 text-blue-600" />
              <p>
                {t(
                  "merchant.integrations.meta.insightsTitle",
                  "Meta-Insights manuell aktualisieren"
                )}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleSyncMeta}
              disabled={syncing}
            >
              {syncing
                ? t(
                    "merchant.integrations.meta.syncingButton",
                    "SynchronisiereÔÇª"
                  )
                : t(
                    "merchant.integrations.meta.syncButton",
                    "Meta-Daten jetzt synchronisieren"
                  )}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={syncDialog.open}
        onOpenChange={(open) =>
          !open &&
          setSyncDialog({
            open: false,
            actionId: null,
            actionTitle: "",
            metaCampaignName: "",
          })
        }
        type="info"
        title={t(
          "merchant.integrations.meta.syncActionDialogTitle",
          "Meta-Daten f├╝r Aktion laden"
        )}
        description={
          syncDialog.actionId ? (
            <div className="space-y-2 text-xs text-gray-700">
              <p>
                {t(
                  "merchant.integrations.meta.syncActionDialogDesc",
                  "F├╝r die Aktion \"{{action}}\" und die verkn├╝pfte Meta-Kampagne \"{{campaign}}\" werden die Leistungsdaten der letzten 12 Monate aus Meta importiert.",
                  {
                    action: syncDialog.actionTitle,
                    campaign: syncDialog.metaCampaignName || "-",
                  }
                )}
              </p>
              {syncing && (
                <div className="space-y-1">
                  <div className="w-full h-1.5 rounded bg-gray-200 overflow-hidden">
                    <div className="h-1.5 w-full bg-blue-500 animate-pulse" />
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {t(
                      "merchant.integrations.meta.syncActionDialogProgress",
                      "Meta-Daten werden geladenÔÇª"
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            ""
          )
        }
        confirmText={t(
          "merchant.integrations.meta.syncActionDialogConfirm",
          "Jetzt aus Meta laden"
        )}
        cancelText={t("common.cancel", "Abbrechen")}
        loading={syncing}
        onConfirm={handleConfirmSyncAction}
        onCancel={() =>
          setSyncDialog({
            open: false,
            actionId: null,
            actionTitle: "",
            metaCampaignName: "",
          })
        }
      />
    </div>
  );
};

const MetaCapiSettings = () => {
  const { t } = useTranslation();
  const { token, setCompany } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [defaultLeadEventName, setDefaultLeadEventName] = useState("Lead");
  const [metaDefaultConversionValue, setMetaDefaultConversionValue] =
    useState("");
  const [metaCurrency, setMetaCurrency] = useState("EUR");
  const [dpoEnabled, setDpoEnabled] = useState(false);
  const [dpoCountry, setDpoCountry] = useState(0);
  const [dpoState, setDpoState] = useState(0);
  const [metaPixelId, setMetaPixelId] = useState("");
  const [metaAccessToken, setMetaAccessToken] = useState("");
  const [metaTestCode, setMetaTestCode] = useState("");
  const [metaTestModeEnabled, setMetaTestModeEnabled] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const capi = await api.getCompanyCapi(token);
        if (!mounted) return;
        setMetaPixelId(capi.metaPixelId || "");
        setMetaAccessToken(capi.metaAccessToken || "");
        setMetaTestCode(capi.metaTestCode || "");
        setMetaTestModeEnabled(!!capi.metaTestModeEnabled);
        setMetaCurrency(capi.metaCurrency || "EUR");
        setDefaultLeadEventName(capi.defaultLeadEventName || "Lead");
        setMetaDefaultConversionValue(
          capi.metaDefaultConversionValue !== null &&
            capi.metaDefaultConversionValue !== undefined
            ? String(capi.metaDefaultConversionValue)
            : ""
        );
        const dpo = capi.dataProcessing || {};
        setDpoEnabled(!!dpo.enabled);
        setDpoCountry(
          typeof dpo.country === "number" ? dpo.country : 0
        );
        setDpoState(typeof dpo.state === "number" ? dpo.state : 0);
      } catch (e) {
        // ignore
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.updateCompanyCapi(token, {
        metaPixelId: metaPixelId || null,
        metaAccessToken: metaAccessToken || null,
        metaTestCode: metaTestCode || null,
        metaTestModeEnabled: !!metaTestModeEnabled,
        metaCurrency: metaCurrency || null,
        defaultLeadEventName: defaultLeadEventName || "Lead",
        metaDefaultConversionValue:
          metaDefaultConversionValue === "" ||
          metaDefaultConversionValue === null
            ? null
            : parseFloat(metaDefaultConversionValue),
        dataProcessing: {
          enabled: !!dpoEnabled,
          country: Number.isFinite(dpoCountry) ? dpoCountry : 0,
          state: Number.isFinite(dpoState) ? dpoState : 0,
        },
      });
      toast({ title: t("capi.saved") });
      // optional: update company context if needed
      setCompany((c) => ({ ...(c || {}), metaCurrency }));
    } catch (e) {
      toast({
        title: t("org.error", { defaultValue: "Fehler" }),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-3 sm:p-4 rounded shadow text-xs text-gray-500">
        {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="bg-white p-3 sm:p-4 rounded shadow text-sm space-y-3">
      <form onSubmit={handleSave} className="space-y-3">
        <div>
          <div className="text-[10px] text-gray-500 mb-1">
            {t("capi.defaults.leadEventName")}
          </div>
          <input
            className="w-full border rounded px-3 py-2 text-sm"
            value={defaultLeadEventName}
            onChange={(e) => setDefaultLeadEventName(e.target.value)}
            placeholder="Lead"
          />
          <p className="text-[10px] text-gray-500 mt-1">
            {t("capi.defaults.leadEventNameHelp")}
          </p>
        </div>

        <div>
          <div className="text-[10px] text-gray-500 mb-1">
            {t("capi.defaults.defaultValue")}
          </div>
          <input
            type="number"
            step="0.01"
            className="w-full border rounded px-3 py-2 text-sm"
            value={metaDefaultConversionValue}
            onChange={(e) => setMetaDefaultConversionValue(e.target.value)}
            placeholder={t("capi.defaults.defaultValuePlaceholder")}
          />
          <p className="text-[10px] text-gray-500 mt-1">
            {t("capi.defaults.defaultValueHelp")}
          </p>
        </div>

        <div>
          <div className="text-[10px] text-gray-500 mb-1">
            {t("capi.currency")}
          </div>
          <select
            className="w-full border rounded px-3 py-2 text-sm"
            value={metaCurrency}
            onChange={(e) => setMetaCurrency(e.target.value)}
          >
            <option value="EUR">EUR (Euro)</option>
            <option value="USD">USD ($)</option>
          </select>
          <p className="text-[10px] text-gray-500 mt-1">
            {t("capi.currencyHelp")}
          </p>
        </div>

        <div>
          <div className="text-[10px] text-gray-500 mb-1">
            {t("capi.dpo.title")}
          </div>
          <div className="flex items-center gap-2 mb-1">
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={dpoEnabled}
                onChange={(e) => setDpoEnabled(e.target.checked)}
              />
              {t("capi.dpo.enabled")}
            </label>
            <input
              type="number"
              className="border rounded px-2 py-1 text-xs w-24"
              value={dpoCountry}
              onChange={(e) =>
                setDpoCountry(parseInt(e.target.value || "0", 10))
              }
              placeholder={t("capi.dpo.country")}
            />
            <input
              type="number"
              className="border rounded px-2 py-1 text-xs w-24"
              value={dpoState}
              onChange={(e) =>
                setDpoState(parseInt(e.target.value || "0", 10))
              }
              placeholder={t("capi.dpo.state")}
            />
          </div>
          <p className="text-[10px] text-gray-500">
            {t("capi.dpo.help")}
          </p>
        </div>

        <div>
          <div className="text-[10px] text-gray-500 mb-1">
            {t("capi.pixelId")}
          </div>
          <input
            className="w-full border rounded px-3 py-2 text-sm"
            value={metaPixelId}
            onChange={(e) => setMetaPixelId(e.target.value)}
            placeholder="123456789012345"
          />
          <p className="text-[10px] text-gray-500 mt-1">
            {t("capi.pixelIdHelpLine1")}
            <br />
            {t("capi.pixelIdHelpLine2")}
          </p>
        </div>

        <div>
          <div className="text-[10px] text-gray-500 mb-1">
            {t("capi.accessToken")}
          </div>
          <input
            className="w-full border rounded px-3 py-2 text-sm"
            value={metaAccessToken}
            onChange={(e) => setMetaAccessToken(e.target.value)}
            placeholder="EAAG..."
          />
          <p className="text-[10px] text-gray-500 mt-1">
            {t("capi.accessTokenHelpLine1")}
            <br />
            {t("capi.accessTokenHelpLine2")}
          </p>
        </div>

        <div>
          <div className="text-[10px] text-gray-500 mb-1">
            {t("capi.testEventCode")}
          </div>
          <input
            className="w-full border rounded px-3 py-2 text-sm"
            value={metaTestCode}
            onChange={(e) => setMetaTestCode(e.target.value)}
            placeholder="TEST123"
          />
          <p className="text-[10px] text-gray-500 mt-1">
            {t("capi.testEventCodeHelpLine1")}
            <br />
            {t("capi.testEventCodeHelpLine2")}
          </p>
          <label className="mt-2 flex items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={metaTestModeEnabled}
              onChange={(e) => setMetaTestModeEnabled(e.target.checked)}
            />
            {t("capi.testModeEnabled", {
              defaultValue: "Test Events aktiv (sendet Test Event Code)",
            })}
          </label>
          <div className="mt-1 text-[11px] text-gray-500">
            {t("capi.testModeEnabledHint", {
              defaultValue:
                "Nur aktivieren, wenn du im Meta Events Manager im Testmodus debuggen willst.",
            })}
          </div>
        </div>

        <p className="text-[10px] text-gray-500">
          {t("capi.noteEncrypted")}
        </p>

        <Button type="submit" size="sm" disabled={saving} className="mt-1">
          {saving ? t("capi.saving") : t("capi.save")}
        </Button>
      </form>
    </div>
  );
};

const MetaCapiStandalone = ({ onBack }) => {
  const { t } = useTranslation();

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-blue-600 hover:text-blue-800 mb-1"
      >
        2 {t("nav.integrations", "Integrationen")}
      </button>
      <div className="flex items-center gap-3 mb-2">
        <MetaLogo className="h-9 w-9" />
        <div>
          <h1 className="text-xl font-semibold">
            {t("capi.title", "Meta Conversions API")}
          </h1>
          <p className="text-xs text-gray-600">
            {t(
              "merchant.integrations.meta.capiDescription",
              "Nutze die Meta Conversions API, um serverseitige Events (Scans, Leads, Einl├Âsungen) DSGVO-konform an Meta zu senden."
            )}
          </p>
        </div>
      </div>

      <div className="bg-white rounded shadow p-3 sm:p-4">
        <MetaCapiSettings />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Shore Integration Settings
// ─────────────────────────────────────────────────────────────────────────────
const ShoreIntegrationSettings = ({ onBack }) => {
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAction, setSavingAction] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const [webhookUrl, setWebhookUrl] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [selectedActionId, setSelectedActionId] = useState("");
  const [savedActionId, setSavedActionId] = useState("");
  const [appointmentTypeFilter, setAppointmentTypeFilter] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [hasWebhookSecret, setHasWebhookSecret] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const selectedCampaign = campaigns.find((c) => c.id === selectedCampaignId);
  const actions = selectedCampaign?.action ?? [];

  useEffect(() => {
    if (!token) return;
    let mounted = true;
    (async () => {
      try {
        const resp = await api.shoreGetConfig(token);
        if (!mounted) return;
        setWebhookUrl(resp?.webhookUrl || "");
        setCampaigns(Array.isArray(resp?.campaigns) ? resp.campaigns : []);
        if (resp?.config) {
          setAppointmentTypeFilter(resp.config.appointmentTypeFilter || "");
          setHasWebhookSecret(!!resp.config.hasWebhookSecret);
          if (resp.config.actionId) {
            setSelectedActionId(resp.config.actionId);
            setSavedActionId(resp.config.actionId);
            const owner = (resp.campaigns || []).find((c) =>
              c.action?.some((a) => a.id === resp.config.actionId)
            );
            if (owner) setSelectedCampaignId(owner.id);
          }
        }
      } catch (e) {
        toast({ title: "Fehler", description: e.message, variant: "destructive" });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const handleSave = async () => {
    if (!token) return;
    setSaving(true);
    try {
      await api.shoreSaveConfig(token, {
        actionId: selectedActionId || null,
        appointmentTypeFilter,
        ...(webhookSecret.trim() ? { webhookSecret: webhookSecret.trim() } : {}),
      });
      if (webhookSecret.trim()) { setHasWebhookSecret(true); setWebhookSecret(""); }
      setSavedActionId(selectedActionId);
      toast({ title: "Gespeichert" });
    } catch (e) {
      toast({ title: "Fehler", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!token || !testEmail) return;
    setSendingTest(true);
    try {
      await api.shoreTestEmail(token, testEmail, selectedActionId || undefined);
      toast({ title: `Test-E-Mail an ${testEmail} gesendet` });
    } catch (e) {
      toast({ title: "Fehler", description: e.message, variant: "destructive" });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <div className="text-sm text-muted-foreground">Laden…</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-5">
      <button type="button" onClick={onBack} className="text-xs text-blue-600 hover:text-blue-800">
        ← Integrationen
      </button>

      {/* Header */}
      <div className="flex items-center gap-3">
        <ShoreLogo className="h-10 w-10" />
        <div>
          <h1 className="text-xl font-semibold">Shore Terminbuchung</h1>
          <p className="text-xs text-muted-foreground">
            Bei jeder Buchung über shore.com bekommt der Kunde automatisch eine E-Mail mit einem Link zu deiner Aktion.
          </p>
        </div>
      </div>

      {/* Webhook URL */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-sm font-medium text-emerald-900">
            <Wifi className="h-4 w-4" />
            Deine Webhook-URL für shore.com
          </div>
          <button
            type="button"
            onClick={() => setShowGuide((v) => !v)}
            className="flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900"
          >
            Wo trage ich das ein?
            {showGuide ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        </div>

        {webhookUrl ? (
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-emerald-200 rounded px-3 py-2 text-xs font-mono break-all">{webhookUrl}</code>
            <button
              type="button"
              className="flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 whitespace-nowrap"
              onClick={() => {
                navigator.clipboard.writeText(webhookUrl);
                setCopiedUrl(true);
                setTimeout(() => setCopiedUrl(false), 2000);
                toast({ title: "URL kopiert" });
              }}
            >
              {copiedUrl ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copiedUrl ? "Kopiert" : "Kopieren"}
            </button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">URL erscheint nach dem ersten Speichern.</p>
        )}

        {showGuide && (
          <div className="mt-1 bg-white border border-emerald-200 rounded-lg p-4 space-y-4">
            <p className="text-sm font-semibold">Schritt-für-Schritt: Webhook in shore.com einrichten</p>
            <ol className="space-y-3 text-xs text-gray-700">
              {[
                { step: 1, title: "In shore.com einloggen", desc: <span>Gehe zu <a href="https://app.shore.com" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">app.shore.com</a> und melde dich an.</span> },
                { step: 2, title: "Einstellungen öffnen", desc: <span>Klicke oben rechts auf dein Profilbild → <strong>Einstellungen</strong>.</span> },
                { step: 3, title: "Integrationen auswählen", desc: <span>Im linken Menü auf <strong>Integrationen</strong> oder <strong>API &amp; Webhooks</strong> klicken.</span> },
                { step: 4, title: "Neuen Webhook anlegen",
                  desc: (
                    <span>
                      Klicke auf <strong>Webhook hinzufügen</strong> und füge diese URL ein:
                      {webhookUrl && (
                        <span className="flex items-center gap-2 mt-1.5">
                          <code className="flex-1 bg-gray-50 border rounded px-2 py-1 text-[10px] font-mono break-all">{webhookUrl}</code>
                          <button type="button" className="text-[10px] text-emerald-700 font-medium whitespace-nowrap" onClick={() => { navigator.clipboard.writeText(webhookUrl); setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 2000); }}>{copiedUrl ? "Kopiert" : "Kopieren"}</button>
                        </span>
                      )}
                    </span>
                  ),
                },
                { step: 5, title: "Ereignis: Buchung bestätigt", desc: <span>Wähle <strong>Appointment created</strong> / <strong>Buchung bestätigt</strong>. Falls nicht verfügbar: <strong>Alle Ereignisse</strong>.</span> },
                { step: 6, title: "Speichern – fertig!", desc: <span>Klicke <strong>Speichern</strong>. Ab sofort erhalten Kunden automatisch eine E-Mail nach jeder Buchung.</span> },
              ].map(({ step, title, desc }) => (
                <li key={step} className="flex gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-[10px]">{step}</span>
                  <div><div className="font-semibold mb-0.5">{title}</div><div className="text-gray-500">{desc}</div></div>
                </li>
              ))}
            </ol>
            <p className="text-[10px] text-gray-400 pt-2 border-t border-gray-100">
              Nicht gefunden? Kontaktiere den <a href="https://hilfe.shore.com" target="_blank" rel="noopener noreferrer" className="underline">shore.com Support</a>.
            </p>
          </div>
        )}
      </div>

      {/* Action picker */}
      <div className="bg-background border rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">Verknüpfte Aktion</h2>
        </div>
        <p className="text-xs text-muted-foreground -mt-2">
          Wähle eine bestehende ScanMirein-Aktion. Kunden erhalten bei Buchung einen Link zu dieser Aktion per E-Mail.
        </p>

        {campaigns.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            Keine Kampagnen gefunden. Erstelle zuerst eine Kampagne mit einer Aktion.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium">Kampagne</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={selectedCampaignId}
                onChange={(e) => { setSelectedCampaignId(e.target.value); setSelectedActionId(""); }}
              >
                <option value="">-- Kampagne wählen --</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.active === false ? " (inaktiv)" : ""}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Aktion</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={selectedActionId}
                disabled={!selectedCampaignId}
                onChange={(e) => setSelectedActionId(e.target.value)}
              >
                <option value="">-- Aktion wählen --</option>
                {actions.map((a) => (
                  <option key={a.id} value={a.id}>{a.title}{a.active === false ? " (inaktiv)" : ""}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {selectedActionId && (
          <div className="flex items-center justify-between">
            {selectedActionId === savedActionId ? (
              <div className="flex items-center gap-1.5 text-xs text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Aktion gespeichert
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  Nicht gespeichert
                </div>
                <Button
                  size="sm"
                  disabled={savingAction}
                  onClick={async () => {
                    setSavingAction(true);
                    try {
                      await api.shoreSaveConfig(token, { actionId: selectedActionId, appointmentTypeFilter });
                      setSavedActionId(selectedActionId);
                      toast({ title: "Aktion gespeichert" });
                    } catch (e) {
                      toast({ title: "Fehler", description: e.message, variant: "destructive" });
                    } finally {
                      setSavingAction(false);
                    }
                  }}
                >
                  {savingAction ? "Speichern…" : "Aktion speichern"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filter + secret */}
      <div className="bg-background border rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">Erweiterte Einstellungen</h2>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium">Termintyp-Filter</label>
          <input
            type="text"
            className="w-full border rounded px-3 py-2 text-sm"
            value={appointmentTypeFilter}
            onChange={(e) => setAppointmentTypeFilter(e.target.value)}
            placeholder="z. B. Probetraining (leer = alle Buchungen)"
          />
          <p className="text-[10px] text-muted-foreground">
            Nur Buchungen, deren Termintyp diesen Begriff enthält, lösen eine E-Mail aus.
          </p>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium">
            Webhook Secret
            {hasWebhookSecret && <span className="ml-2 text-[10px] text-emerald-600">gespeichert</span>}
          </label>
          <input
            type="password"
            className="w-full border rounded px-3 py-2 text-sm"
            value={webhookSecret}
            onChange={(e) => setWebhookSecret(e.target.value)}
            placeholder={hasWebhookSecret ? "Leer lassen, um beizubehalten" : "Optional: HMAC-Secret von shore.com"}
          />
          <p className="text-[10px] text-muted-foreground">
            Nur setzen, wenn shore.com eine Webhook-Signatur anbietet.
          </p>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Speichern…" : "Konfiguration speichern"}
          </Button>
        </div>
      </div>

      {/* Test email */}
      <div className="bg-background border rounded-lg p-4 space-y-3">
        <h2 className="text-sm font-semibold">Test-E-Mail senden</h2>
        <p className="text-xs text-muted-foreground">
          Sende eine Vorschau der Buchungs-E-Mail an eine beliebige Adresse.
        </p>
        <div className="flex gap-2">
          <input
            type="email"
            className="flex-1 border rounded px-3 py-2 text-sm"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="test@beispiel.de"
          />
          <Button variant="outline" onClick={handleTestEmail} disabled={sendingTest || !testEmail}>
            {sendingTest ? "Senden…" : "Test senden"}
          </Button>
        </div>
      </div>

      {/* Flow overview */}
      <div className="bg-muted/40 border rounded-lg p-4 space-y-2">
        <h2 className="text-sm font-semibold">Ablauf auf einen Blick</h2>
        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground items-center">
          <span className="flex items-center gap-1 bg-background border rounded px-2 py-1"><Calendar className="h-3 w-3" /> Kunde bucht</span>
          <span>→</span>
          <span className="flex items-center gap-1 bg-background border rounded px-2 py-1"><Wifi className="h-3 w-3" /> Shore sendet Webhook</span>
          <span>→</span>
          <span className="flex items-center gap-1 bg-background border rounded px-2 py-1"><Settings className="h-3 w-3" /> ScanMirein prüft Filter</span>
          <span>→</span>
          <span className="flex items-center gap-1 bg-background border rounded px-2 py-1"><Link2 className="h-3 w-3" /> E-Mail mit Aktionslink</span>
          <span>→</span>
          <span className="flex items-center gap-1 bg-background border rounded px-2 py-1"><CheckCircle2 className="h-3 w-3" /> Kunde löst ein</span>
        </div>
        <p className="text-[10px] text-muted-foreground">Vollautomatisch — kein manueller Aufwand.</p>
      </div>
    </div>
  );
};

export default IntegrationsPage;
